import React from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import { Button } from './ui/button';
import { getGenericErrorMessage } from '../shared/utils/errors';

interface Props {
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
}

/**
 * Production app-level error boundary.
 *
 * Catches render-time crashes in the React tree (the class of failure the global
 * mutation/query error toasts cannot reach — those only handle rejected API
 * promises, not a component throwing during render) and shows PanLuma's standard
 * unexpected-error message: a friendly, translated line that tells the user to
 * report it via the support widget, with a timestamp reference. This mirrors the
 * message every API failure shows, so a white-screen crash never leaves the user
 * with a raw stack trace or a blank page.
 *
 * The developer-facing `ErrorBoundaryDebug` (red box + component stack) is a
 * separate, dev-only tool — this one is what production users see.
 */
export class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Diagnostics for the browser console / error reporters. Not user-facing.
    console.error('[ErrorBoundary] Unhandled render error:', {
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
    });
  }

  render() {
    if (this.state.hasError) {
      return <AppErrorFallback />;
    }
    return this.props.children;
  }
}

function AppErrorFallback() {
  const { t } = useTranslation();

  return (
    <div className="flex min-h-screen w-full flex-col items-center justify-center gap-4 bg-background p-6 text-center">
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10">
        <AlertTriangle className="h-6 w-6 text-destructive" />
      </div>
      <h1 className="text-lg font-semibold text-foreground">
        {t('common:errorBoundary.title')}
      </h1>
      <p className="max-w-md text-sm text-muted-foreground">
        {getGenericErrorMessage()}
      </p>
      <Button onClick={() => window.location.reload()} className="mt-2">
        {t('common:errorBoundary.reload')}
      </Button>
    </div>
  );
}
